import Ship from "@/model/Ship";
import Configuration from "@/model/Configuration";
import EconomicData from "@/model/EconomicData";
import EconomicRepo from "@/repositories/economic/EconomicRepo";
import FuelEconomicData from "@/model/FuelEconomicData";
import CalcEconomicDataService from "@/services/CalcEconomicDataService";
import { propulsionTypeCodes } from "../utils/constants";

class EconomicService {
  private static instance: EconomicService;
  private repo: EconomicRepo;

  private constructor() {
    this.repo = new EconomicRepo(this);
  }

  public static getInstance() {
    if (!EconomicService.instance) {
      EconomicService.instance = new EconomicService();
    }
    return EconomicService.instance;
  }

  public saveEconomicData(shipId: string, configuration: Configuration, economicData: EconomicData) {
    //console.log("EconomicService.saveEconomicData", shipId, configuration, economicData);
    economicData.fuelEconomicDatasInitial.forEach((element) => {
      if (element.consumptionInTonsName == "MWh") {
        element.consumptionInTons = false;
      } else {
        element.consumptionInTons = true;
      }
    });

    if (economicData.isBase != true) {
      economicData.fuelEconomicDatasModified.forEach((element) => {
        if (element.consumptionInTonsName == "MWh") {
          element.consumptionInTons = false;
        } else {
          element.consumptionInTons = true;
        }
      });
    }

    //console.log("EconomicService.saveEconomicData", shipId);
    return this.repo.saveEconomicRepo(shipId, configuration, economicData);
  }

  public cloneEconomicData(shipId: string, configurationCreatedId: string, economicData: EconomicData) {
    const newConfiguration = new Configuration();
    newConfiguration.id = configurationCreatedId;
    this.saveEconomicData(shipId, newConfiguration, economicData);
  }

  public getEconomicData(ship: Ship, configuration: Configuration) {
    //console.log("EconomicService.getEconomicData", ship, configuration);
    this.repo.getEconomicData(ship, configuration);
  }

  public actualizeEconomicData(ship: Ship, configuration: Configuration) {
    // console.log(
    //   "EconomicService.actualizeEconomicData. Vamos a actualizar los datos económicos",
    //   "para la configuración",
    //   configuration.name,
    //   ". IsBase = ",
    //   configuration.isBase
    // );
    const result = configuration.economicData ? configuration.economicData : new EconomicData();

    if (configuration.isBase) {
      result.isBase = true;
      result.fuelEconomicDatasInitial = this.createFuelEconomicDataArrayFromConfiguration(configuration);
      result.fuelEconomicDatasModified = new Array<FuelEconomicData>();
    } else {
      result.isBase = false;
      const base: Configuration | void = this.getBaseConfigurationForShip(ship);
      if (base) {
        // console.log(
        //   "EconomicService.actualizeEconomicData. Encontrada configuración base (",
        //   base.name,
        //   ") para",
        //   ship.name
        // );
        result.fuelEconomicDatasInitial = this.createFuelEconomicDataArrayFromConfiguration(base);
      } else {
        //console.log("EconomicService.actualizeEconomicData. NO Encontrada configuración base para", ship.name);
        result.fuelEconomicDatasInitial = new Array<FuelEconomicData>();
      }

      result.fuelEconomicDatasModified = this.createFuelEconomicDataArrayFromConfiguration(configuration);
    }
    configuration.economicData = result;
    return this.saveEconomicData(ship.id, configuration, result);
  }

  private getBaseConfigurationForShip(ship: Ship): Configuration | void {
    let result;
    // console.log(
    //   "EconomicService.getBaseConfigurationForShip Vamos a buscar la",
    //   "configuración base para el buque",
    //   ship.name
    // );
    ship.configurations.forEach((configuration) => {
      //console.log("EconomicService.getBaseConfigurationForShip", configuration.name, ". IsBase", configuration.isBase);
      if (configuration.isBase) {
        result = configuration;
      }
    });
    return result;
  }

  private createFuelEconomicDataArrayFromConfiguration(configuration: Configuration): Array<FuelEconomicData> {
    const result = new Array<FuelEconomicData>();
    configuration.MainEngineEmissions.forEach((mainEngine) => {
      const fuelData = this.getNewFuelData(mainEngine.fuelType, configuration);
      result.push(fuelData);

      if (mainEngine.propulsionType === propulsionTypeCodes.Dual) {
        const fuelData2 = this.getNewFuelData(mainEngine.secondFuelType, configuration);
        result.push(fuelData2);
      }
    });

    configuration.AuxiliaryEngineEmissions.forEach((auxEngine) => {
      const fuelData = this.getNewFuelData(auxEngine.fuelType, configuration);
      result.push(fuelData);

      if (auxEngine.propulsionType === propulsionTypeCodes.Dual) {
        const fuelData2 = this.getNewFuelData(auxEngine.secondFuelType, configuration);
        result.push(fuelData2);
      }
    });

    return result;
  }

  private getNewFuelData(fuelName: string, configuration: Configuration) {
    const result = new FuelEconomicData();
    result.name = fuelName + " (" + configuration.name + ")";
    result.code = fuelName;
    return result;
  }

  public addedEconomicData(ship: Ship, configuration: Configuration, id: string, data: any) {
    //console.log("EconomicService.addedEconomicData", ship.name, configuration.name, id, data);
    const economicData = {
      id: id,
      ...data,
    };
    configuration.economicData = economicData;
  }

  public modifiedEconomicData(ship: Ship, configuration: Configuration, id: string, data: any) {
    //console.log("EconomicService.modifiedEconomicData", ship.name, configuration.name, id, data);
    const economicData = {
      id: id,
      ...data,
    };
    configuration.economicData = economicData;
  }

  public getEEXIDataForEconomic(configuration: Configuration, ship: Ship) {
    const baseConfiguration = configuration.isBase ? configuration : this.getBaseConfigurationForShip(ship);
    const baseAttainedEEXI = baseConfiguration ? baseConfiguration.attainedEEXI : 0;
    const configurationBaseName = baseConfiguration ? baseConfiguration.name : "";
    return { configurationEEXI: configuration.attainedEEXI, baseEEXI: baseAttainedEEXI, configurationName: configuration.name, configurationBaseName: configurationBaseName} ;
  }

  public calcEconomicData(configuration: Configuration, ship: Ship) {
    const eexiData = this.getEEXIDataForEconomic(configuration, ship);
    return CalcEconomicDataService.getInstance().calcEconomicData(
      configuration.economicData,
      eexiData.configurationEEXI,
      eexiData.baseEEXI
    );
  }
}

export default EconomicService;
